import { useState, useCallback } from "react";
import { toast } from "sonner";
import { Coins, Lightbulb, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { quoteCredits } from "@/lib/api";

/**
 * useCreditGate — ask "Questa operazione utilizzerà N crediti." before a paid action.
 * gate(action, fn) → quotes; free actions run immediately; otherwise opens the dialog.
 * fn receives a stable op_id so retries never double-charge.
 */
export function useCreditGate() {
  const [state, setState] = useState(null); // { quote, fn, op_id, busy }

  const gate = useCallback(async (action, fn, quantity = 1) => {
    let q;
    try {
      q = await quoteCredits(action);
    } catch {
      toast.error("Impossibile verificare il costo: riprova prima di avviare l’elaborazione.");
      return undefined;
    }
    if (!q.free && !q.variable && !q.feature_required && quantity > 1) {
      q = { ...q, credits: q.credits * quantity,
        allowed: q.allowed && q.available >= q.credits * quantity,
        message: `${quantity} operazioni: massimo ${Number((q.credits * quantity).toFixed(3))} gettoni. Gli invii non accettati non vengono addebitati.` };
    }
    if (q.free) return fn(undefined);
    const op_id = `${action}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setState({ quote: q, fn, op_id, busy: false });
    return undefined;
  }, []);

  const proceed = async () => {
    if (!state) return;
    setState((s) => ({ ...s, busy: true }));
    try {
      await state.fn(state.op_id);
      setState(null);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      if (e?.response?.status === 402) toast.error(msg || "Crediti insufficienti");
      setState(null);
      throw e;
    }
  };

  const dialog = (
    <Dialog open={!!state} onOpenChange={(o) => !o && !state?.busy && setState(null)}>
      <DialogContent className="bg-[#0B1120] border-white/10 text-slate-100 max-w-md" data-testid="credit-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/25 text-amber-300 flex items-center justify-center"><Coins size={15} /></span>
            Gettoni Nadia
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs">Conferma prima di usare i gettoni.</DialogDescription>
        </DialogHeader>
        {state && (
          <div className="space-y-3">
            <div className="text-sm text-slate-300">{state.quote.label}</div>
            <div className="font-heading text-xl font-bold text-slate-50" data-testid="credit-confirm-message">{state.quote.message}</div>
            <div className="text-xs text-slate-400">Disponibili adesso: <span className="text-slate-100 font-semibold" data-testid="credit-confirm-available">{state.quote.available}</span>{!state.quote.variable && <> · dopo: {Math.max(state.quote.available - state.quote.credits, 0)}</>}</div>
            {!state.quote.allowed && (
              <div className="rounded-xl border border-rose-500/25 bg-rose-500/[0.06] p-3 text-xs text-rose-200" data-testid="credit-confirm-blocked">
                {state.quote.feature_required || state.quote.pricing_pending ? state.quote.message : state.quote.has_plan ? "Gettoni insufficienti. Ricarica il saldo per continuare." : "Attiva un piano per usare questa funzione."}
              </div>
            )}
            {state.quote.saving_tip && (
              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.04] p-3 text-xs text-slate-300 flex gap-2" data-testid="credit-saving-tip">
                <Lightbulb size={14} className="text-cyan-300 shrink-0 mt-0.5" /> <span><span className="text-cyan-200 font-semibold">Nadia:</span> {state.quote.saving_tip}</span>
              </div>
            )}
            <div className="text-[11px] text-slate-500">I gettoni vengono scalati solo se l’operazione va a buon fine. La parte prenotata e non utilizzata viene restituita.</div>
            <div className="flex gap-2 justify-end pt-1">
              <Button variant="ghost" onClick={() => setState(null)} disabled={state.busy} className="text-slate-400 hover:text-slate-200" data-testid="credit-cancel">Annulla</Button>
              <Button onClick={proceed} disabled={state.busy || !state.quote.allowed} className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-semibold hover:from-cyan-300 hover:to-violet-400" data-testid="credit-proceed">
                {state.busy && <Loader2 size={14} className="mr-2 animate-spin" />} Procedi
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  return { gate, dialog };
}
